// ── PHONE SIGN-IN ────────────────────────────────────────────────────────────
// The reCAPTCHA half of Firebase phone auth, which is the half that breaks.
//
// A RecaptchaVerifier is NOT a plain object. It renders a widget into a real
// DOM node, registers callbacks on a global `grecaptcha`, and holds a token
// that is spent by the first successful send. Four consequences drive
// everything below:
//
//   1. THE CONTAINER MUST NOT BELONG TO REACT. grecaptcha keeps a reference to
//      the node it rendered into; a re-render that replaces or moves that node
//      leaves the widget pointing at a detached element. The container lives in
//      public/index.html, OUTSIDE #root, so React can never touch it.
//
//   2. IT OUTLIVES REACT. Unmounting does not unregister any of it, so a second
//      visit to the page finds a live verifier attached to a spent widget.
//
//   3. THE TOKEN IS SINGLE-USE. Reusing one verifier for "resend" fails with
//      `auth/captcha-check-failed`, or hangs — the classic "the first code
//      arrives, the second never does" bug. Every send therefore gets a FRESH
//      verifier, and the old one is destroyed first.
//
//   4. `clear()` DOES NOT EMPTY THE CONTAINER. It tears down the widget but can
//      leave the injected iframe behind, and rendering a second widget into a
//      node that still holds one throws "reCAPTCHA has already been rendered in
//      this element". The container is emptied by hand.
//
// ── ABOUT `captchaResponse: "NO_RECAPTCHA"` ─────────────────────────────────
// Seeing that in the sendVerificationCode payload is NOT a fault and not
// something this file can change. It is a placeholder the SDK sends itself:
// `handleRecaptchaFlow` takes the reCAPTCHA v2 path whenever reCAPTCHA
// Enterprise is not enabled for the project's PHONE_PROVIDER, and that path
// calls `injectRecaptchaFields(..., isFakeToken = true)`, which sets
// `captchaResponse` to the literal string "NO_RECAPTCHA" and `recaptchaVersion`
// to "RECAPTCHA_ENTERPRISE" regardless. Both strings appear in the payload of a
// SUCCESSFUL send too. The real v2 token travels in `recaptchaToken`, which is
// the field to look at when diagnosing.
//
// A send that fails while showing those strings is failing for a reason the
// response body states — most often `auth/invalid-app-credential`, which means
// the domain is not in Authorized domains, the Phone provider is off, or the
// API key is restricted away from Identity Toolkit. See RECAPTCHA_TOKEN_HINT
// below, which reads the token back so those can be told apart.
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from './firebase';

/** The id of the div in public/index.html. */
export const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

let _verifier = null;

/**
 * A container with NO widget in it, guaranteed — by replacing the node rather
 * than emptying it.
 *
 * ── WHY A NEW NODE, AND WHY THIS FIXES SIGNING IN TWICE ─────────────────────
 * `RecaptchaVerifier.clear()` reads, in the SDK:
 *
 *   clear() {
 *     this.assertNotDestroyed(); this.destroyed = true;
 *     this._recaptchaLoader.clearedOneInstance();
 *     this.isInvisible || this.container.childNodes.forEach(
 *       e => this.container.removeChild(e));
 *   }
 *
 * It empties the container ONLY when the widget is visible. Ours is
 * `size: 'invisible'`, so grecaptcha's DOM is left in place — and every new
 * RecaptchaVerifier starts with `renderPromise = null`, so the next send calls
 * `grecaptcha.render()` on an element that already carries a widget. That is
 * the "sign in with a phone, sign out, try again" failure: the first attempt of
 * a session works, every later one dies in the vendor script.
 *
 * REPLACED, NOT EMPTIED, for two separate reasons:
 *
 *   · grecaptcha registers a widget against the ELEMENT, not against its
 *     children. Wiping innerHTML leaves that registration intact and the second
 *     render still reports the element as already rendered. A new node has an
 *     identity grecaptcha has never seen.
 *
 *   · Emptying in `clearRecaptcha` is what threw
 *       Uncaught TypeError: Cannot read properties of null (reading 'style')
 *           at recaptcha__en.js
 *     — DOM pulled out from under a challenge that was still animating away.
 *     This runs at the START of building a new widget instead, a moment when
 *     the old one is already destroyed and nothing is on screen.
 *
 * `replaceChild` rather than remove-then-append, so the node keeps its place in
 * the document (immediately after #root, per index.html) instead of migrating
 * to the end of <body> a little further on every sign-in.
 */
function freshContainer() {
  const fresh = document.createElement('div');
  fresh.id = RECAPTCHA_CONTAINER_ID;

  const existing = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (existing?.parentNode) existing.parentNode.replaceChild(fresh, existing);
  else document.body.appendChild(fresh);

  return fresh;
}

/**
 * Destroys the current verifier. Safe at any time, including when there is
 * nothing to clear.
 *
 * IT DOES NOT EMPTY THE CONTAINER, and that omission is deliberate. Wiping
 * `innerHTML` here tears out DOM that grecaptcha still owns and still has
 * timers pointing at, and the script then throws from inside itself:
 *
 *   Uncaught TypeError: Cannot read properties of null (reading 'style')
 *       at recaptcha__en.js
 *
 * — a stack with none of our frames in it, raised after a challenge was
 * dismissed. `verifier.clear()` is Firebase's own teardown and removes what it
 * added; anything it leaves behind is grecaptcha's to manage, not ours.
 */
export function clearRecaptcha() {
  // `window.recaptchaVerifier` and the module handle are the SAME object. The
  // global exists because it is what the Firebase docs and every console
  // debugging session reach for; the module handle is what this file uses, so
  // a caller clearing one never leaves the other pointing at a dead widget.
  const v = _verifier || window.recaptchaVerifier;
  if (v) {
    try { v.clear(); } catch { /* already gone, or never rendered */ }
  }
  _verifier = null;
  window.recaptchaVerifier = null;
}

/**
 * Resets the widget in place, keeping the verifier. Cheaper than a rebuild and
 * the documented way to recover a challenge that was solved-then-rejected —
 * used on a failed send so the next attempt starts from a clean challenge.
 *
 * Falls back to a full teardown: `grecaptcha` is absent until the first widget
 * renders, and on a test-number flow it may never load at all.
 */
export async function resetRecaptcha() {
  const v = _verifier || window.recaptchaVerifier;
  if (!v) return;
  try {
    const widgetId = await v.render();
    if (window.grecaptcha?.reset) window.grecaptcha.reset(widgetId);
    else clearRecaptcha();
  } catch {
    clearRecaptcha();
  }
}

/**
 * Builds a fresh invisible verifier on the shared container.
 *
 * Exported so a caller can warm it up before the user presses anything — the
 * first render fetches the reCAPTCHA script, which is the slowest part of the
 * first send.
 */
export async function initRecaptcha() {
  // REUSED, NOT REBUILT. This used to destroy and recreate the verifier on
  // every send, on the reasoning that the token is single-use — true, but the
  // cure for a spent token is `grecaptcha.reset(widgetId)`, not a new widget.
  // Rebuilding meant tearing down a widget whose challenge could still be on
  // screen, which is what threw "Cannot read properties of null (reading
  // 'style')" from inside recaptcha__en.js on the second attempt after a failed
  // send. One widget lives for the life of the panel and is reset between
  // attempts; see resetRecaptcha and sendVerificationCode.
  if (_verifier && !_verifier.destroyed) return _verifier;
  clearRecaptcha();                       // a destroyed handle, or none at all

  // ── A CLEAN CONTAINER, BEFORE THE VERIFIER ────────────────────────────────
  // Reached only when there is no live verifier, i.e. exactly when a new widget
  // is about to be rendered. See freshContainer for why the node is replaced
  // rather than emptied, and for the second-sign-in bug that requires it.
  //
  // It also guarantees the node exists at all: the id below is resolved by the
  // SDK with getElementById, and a missing node fails as a bare
  // `auth/argument-error` with no hint as to why.
  freshContainer();

  // The id string, not the element: the SDK looks it up itself, which keeps the
  // lookup and the render in the same tick and matches the documented usage.
  _verifier = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
    size: 'invisible',
    // Fired when the challenge solves itself, which for an invisible widget is
    // the normal path. Nothing to do — the send promise is what we await. Named
    // because its absence is a common source of silent stalls.
    callback: () => {},
    // The token died before the send completed. Dropping the verifier means the
    // next attempt builds a clean one rather than reusing a dead token.
    'expired-callback': () => { clearRecaptcha(); },
  });
  window.recaptchaVerifier = _verifier;

  // Rendered explicitly so a misconfigured domain fails HERE, with a real
  // error, rather than inside signInWithPhoneNumber where it surfaces as an
  // opaque internal error.
  await _verifier.render();
  return _verifier;
}

/**
 * Sends an SMS and returns the ConfirmationResult the caller must keep in order
 * to confirm the code.
 *
 * TEST NUMBERS NEED NO SPECIAL CASE. A number registered under Authentication →
 * Sign-in method → Phone → "Phone numbers for testing" takes this exact path:
 * Firebase short-circuits server-side, returns a ConfirmationResult, and sends
 * no SMS. The verifier is still required as an argument and is still rendered —
 * the difference is invisible from here, which is why there is no branch for it.
 * (`auth.settings.appVerificationDisabledForTesting = true` skips reCAPTCHA
 * itself, but that is for automated tests and must never ship enabled.)
 *
 * @param {string} e164  the number, already normalized — see constants/phone
 */
export async function sendVerificationCode(e164) {
  const verifier = await initRecaptcha();
  try {
    return await signInWithPhoneNumber(auth, e164, verifier);
  } catch (err) {
    // A rejected attempt leaves a solved-but-spent token behind. RESET the
    // widget rather than destroying it: the next attempt then gets a fresh
    // token from the same widget, and nothing is removed from the page while
    // grecaptcha may still be animating a challenge away.
    //
    // NOT `clear()` + `= null`. That is the obvious-looking fix and it is the
    // one that broke this before: tearing the widget down while grecaptcha may
    // still be dismissing a challenge throws
    //   Uncaught TypeError: Cannot read properties of null (reading 'style')
    //       at recaptcha__en.js
    // from inside the vendor script, with none of our frames on the stack.
    // `grecaptcha.reset(widgetId)` is the documented recovery and it discards
    // the spent token just as thoroughly. See resetRecaptcha.
    await resetRecaptcha();

    // The backend's own words, attached where a caller can reach them without
    // importing anything. `err.code` alone cannot tell a transient 503 from a
    // misconfigured project — both arrive as opaque auth/… codes.
    try { err.detail = describePhoneError(err); } catch { /* frozen error */ }
    throw err;
  }
}

// ── ERRORS ───────────────────────────────────────────────────────────────────
// Mapped to i18n keys rather than sentences, so the panel translates them and
// the same mapping serves any other caller. Every code here is one Firebase
// actually returns for phone auth; anything unlisted falls through to a generic
// message rather than showing the user a raw `auth/...` string.
const ERROR_KEYS = {
  'auth/invalid-phone-number':      'phone_err_invalid',
  'auth/missing-phone-number':      'phone_err_required',
  'auth/invalid-verification-code': 'phone_err_code_invalid',
  'auth/missing-verification-code': 'phone_err_code_required',
  'auth/code-expired':              'phone_err_code_expired',
  'auth/session-expired':           'phone_err_code_expired',
  'auth/too-many-requests':         'phone_err_too_many',
  'auth/quota-exceeded':            'phone_err_quota',
  'auth/captcha-check-failed':      'phone_err_captcha',
  'auth/missing-recaptcha-token':   'phone_err_captcha',
  'auth/network-request-failed':    'phone_err_network',
  'auth/operation-not-allowed':     'phone_err_not_enabled',
  'auth/user-disabled':             'phone_err_disabled',
  'auth/credential-already-in-use': 'phone_err_in_use',
  'auth/account-exists-with-different-credential': 'phone_err_in_use',
  // The one that actually fires for a misconfigured project. Given its own
  // message because "try again" is useless advice for it — nothing the user can
  // do will help until the domain is authorized.
  'auth/invalid-app-credential':    'phone_err_unauthorized_domain',
  'auth/app-not-authorized':        'phone_err_unauthorized_domain',
  'auth/unauthorized-domain':       'phone_err_unauthorized_domain',
  'auth/argument-error':            'phone_err_generic',
};

export function phoneErrorKey(err) {
  return ERROR_KEYS[err?.code] || 'phone_err_generic';
}

/**
 * What the BACKEND said, as opposed to what the SDK wrapped it in.
 *
 * Firebase collapses every server failure into a handful of `auth/…` codes and
 * hides the response that produced it on `err.customData.serverResponse`. That
 * is the difference between "retry in a minute" and "your project is
 * misconfigured", and it is invisible from the code alone:
 *
 *   · HTTP 503 + no error body  → Identity Toolkit was briefly unavailable.
 *     Surfaces as `auth/internal-error`. Transient; the same call succeeds on
 *     a retry and nothing in this file can prevent it.
 *   · HTTP 400 INVALID_APP_CREDENTIAL → the v2 token was rejected. The domain
 *     is not in Authorized domains, the Phone provider is off, or the browser
 *     API key is restricted away from Identity Toolkit.
 *   · HTTP 400 MISSING_RECAPTCHA_TOKEN → no token was produced at all, which
 *     IS a client-side reCAPTCHA problem and the only one of the three this
 *     file could be at fault for.
 *
 * Returned rather than logged so the caller decides whether to show it.
 */
export function describePhoneError(err) {
  const raw = err?.customData?.serverResponse;
  const server = typeof raw === 'string' ? safeParse(raw) : raw;
  return {
    code: err?.code || null,
    message: err?.message || String(err || ''),
    // Identity Toolkit puts the real reason here, e.g. "INVALID_APP_CREDENTIAL"
    // or "TOO_MANY_ATTEMPTS_TRY_LATER : ...".
    serverMessage: server?.error?.message || null,
    serverStatus: server?.error?.status || server?.error?.code || null,
    transient: err?.code === 'auth/internal-error' && !server?.error?.message,
  };
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}

// ── DIAGNOSTIC ───────────────────────────────────────────────────────────────
// Answers the one question the payload cannot: did the v2 challenge actually
// produce a token? A send that fails WITH a token is a project-configuration
// problem (domain, provider, API key); one that fails WITHOUT is a reCAPTCHA
// problem. `captchaResponse` says nothing either way — see the note at the top.
//
// Call from the console: `await window.__tunifyRecaptchaCheck()`
if (typeof window !== 'undefined') {
  window.__tunifyRecaptchaCheck = async () => {
    const env = {
      origin: window.location.origin,
      host: window.location.hostname,
      // The host the auth iframe is served from. It must be a domain Firebase
      // owns (…firebaseapp.com) or one you have set up as a custom auth domain
      // — NOT simply the site's own hostname.
      authDomain: auth?.config?.authDomain,
      // Both must be listed under Authentication → Settings → Authorized
      // domains. Adding the apex does NOT cover the www host, or vice versa.
      containerFound: !!document.getElementById(RECAPTCHA_CONTAINER_ID),
      containerInReactRoot: !!document.querySelector(`#root #${RECAPTCHA_CONTAINER_ID}`),
    };
    try {
      const v = await initRecaptcha();
      const token = await v.verify();
      clearRecaptcha();
      return {
        ...env,
        grecaptchaLoaded: !!window.grecaptcha,
        tokenReceived: !!token,
        tokenLength: token ? String(token).length : 0,
        // A token here means reCAPTCHA is working and any send failure is a
        // project-configuration or backend problem, not this file's.
        verdict: token
          ? 'reCAPTCHA OK — a failing send is server-side (see describePhoneError on the error)'
          : 'no token — reCAPTCHA itself failed',
      };
    } catch (err) {
      clearRecaptcha();
      return { ...env, failed: describePhoneError(err) };
    }
  };
}
